import React, { useEffect, useMemo, useState } from "react";
import {
    Button, Col, Divider, Dropdown, Input, InputNumber, Menu, Modal, Radio,
    Row, Select, Space, Table, Tag, Typography, message
} from "antd";
import { MoreOutlined } from "@ant-design/icons";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
    listSettlements,
    getSettlement,
    approveSettlement,
    cancelSettlement,
    deleteSettlement,
    updateSettlement,
    listFinancialYears
} from "../../actions/settlement.action";
import { getActiveOperators } from "../../actions/admin.action";
import { activeOperatorsSelector } from "../../selector/admin.selector";
import { isValidArray } from "../../utils/utilities";
import { showConfirm } from "../../components/custom-components/custom-component";
import {
    checkToApproveSettlement,
    checkToCancelSettlement,
    checkToEditSettlement,
    checkToRecordSettlementPayment
} from "../../components/app-header/app.header.module";
import { PaymentsPanel, RecordPaymentModal } from "./payments-panel.component";
import { SettlementInvoicePdfActions } from "../pdfs/settlement-invoice.pdf";
import { computeTotals } from "../../utils/settlement-lines.util";
import useColumnSearch from "../../ui-components/table-column-search";

const { Title, Text } = Typography;
const { Option } = Select;
const ISO_DATE = "YYYY-MM-DD";

const STATUS_COLORS = {
    DRAFT: "default",
    APPROVED: "blue",
    PARTIALLY_SETTLED: "orange",
    SETTLED: "green",
    CANCELLED: "red"
};

const WEEKDAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

// Indian FY months, April-first.
const FY_MONTH_OPTIONS = [
    { num: 4,  name: "April"     }, { num: 5,  name: "May"       },
    { num: 6,  name: "June"      }, { num: 7,  name: "July"      },
    { num: 8,  name: "August"    }, { num: 9,  name: "September" },
    { num: 10, name: "October"   }, { num: 11, name: "November"  },
    { num: 12, name: "December"  }, { num: 1,  name: "January"   },
    { num: 2,  name: "February"  }, { num: 3,  name: "March"     }
];

// Week dropdown options 1..count. `count` comes from weeksInFYMonth so months
// whose tail spills into a 6th bucket expose Week 6 (see the helper for the
// corner case), instead of those trailing days being unreachable.
const buildWeekOptions = (count) =>
    Array.from({ length: Math.max(1, count) }, (_, i) => ({ num: i + 1, label: `Week ${i + 1}` }));

const WEEKDAY_ISO = {
    MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4,
    FRIDAY: 5, SATURDAY: 6, SUNDAY: 7
};

// Plain Indian-grouped 2-decimal number (e.g. 12,34,567.89) — for quantities.
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Money: ₹ + Indian grouping, sign kept before the symbol (e.g. -₹1,234.00).
const inr = (n) => {
    const num = Number(n || 0);
    return (num < 0 ? "-₹" : "₹") + fmtNum(Math.abs(num));
};
const fmtDate = (d) => d ? moment(d).format(ISO_DATE) : "—";
const fmtDateTime = (d) => d ? moment(d).format("YYYY-MM-DD HH:mm") : "—";
const pad2 = (n) => String(n).padStart(2, "0");

const deriveCurrentFY = () => {
    const now = new Date();
    const startYear = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

const yearForFYMonth = (financialYear, month) => {
    const start = parseInt(String(financialYear).split("-")[0], 10);
    return month >= 4 ? start : start + 1;
};

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

// Mirrors settlements-generate.page.js's computePeriod (clipped weekday-aligned weeks).
const computePeriod = (cycle, financialYear, month, week, weekday) => {
    if (!financialYear || !month) return { periodStart: null, periodEnd: null };
    const year = yearForFYMonth(financialYear, month);
    const dim = daysInMonth(year, month);
    const ms = pad2(month);

    if (cycle === "MONTHLY") {
        return { periodStart: `${year}-${ms}-01`, periodEnd: `${year}-${ms}-${pad2(dim)}` };
    }
    const targetIso = WEEKDAY_ISO[(weekday || "MONDAY").toUpperCase()];
    if (!targetIso) return { periodStart: null, periodEnd: null };
    const w = week || 1;
    const day1 = new Date(year, month - 1, 1);
    const day1Iso = ((day1.getDay() + 6) % 7) + 1;
    const backDays = (day1Iso - targetIso + 7) % 7;
    const week1Start = new Date(year, month - 1, 1 - backDays);
    const rawStart = new Date(week1Start);
    rawStart.setDate(week1Start.getDate() + 7 * (w - 1));
    const rawEnd = new Date(rawStart);
    rawEnd.setDate(rawStart.getDate() + 6);
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month - 1, dim);
    if (rawEnd < monthStart || rawStart > monthEnd) {
        return { periodStart: null, periodEnd: null };
    }
    const clippedStart = rawStart < monthStart ? monthStart : rawStart;
    const clippedEnd = rawEnd > monthEnd ? monthEnd : rawEnd;
    const fmt = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    return { periodStart: fmt(clippedStart), periodEnd: fmt(clippedEnd) };
};

// How many weekday-aligned weeks cover the month. When day 1 sits late in its
// bucket the month tail spills into a 6th week — e.g. June with a Tuesday
// settlement day when June 1 is a Monday: week 1 starts May 26 and June 30
// lands in week 6. Mirrors settlements-generate.page.js. Returns 4–6.
const weeksInFYMonth = (financialYear, month, weekday) => {
    if (!financialYear || !month) return 5;
    const year = yearForFYMonth(financialYear, month);
    const dim = daysInMonth(year, month);
    const targetIso = WEEKDAY_ISO[(weekday || "MONDAY").toUpperCase()];
    if (!targetIso) return 5;
    const day1 = new Date(year, month - 1, 1);
    const day1Iso = ((day1.getDay() + 6) % 7) + 1;
    const backDays = (day1Iso - targetIso + 7) % 7;
    const week1Start = new Date(year, month - 1, 1 - backDays);
    const monthEnd = new Date(year, month - 1, dim);
    const dayDiff = Math.round((monthEnd - week1Start) / 86400000);
    return Math.floor(dayDiff / 7) + 1;
};

// Auto-recompute charge for auto_compute / custom lines on qty/unit edits.
const deriveChargeIfAuto = (line) => {
    const auto = line.auto_compute || line.is_custom;
    if (!auto) return line.charge_amount;
    const qty = Number(line.quantity);
    const unit = Number(line.unit_price);
    if (!Number.isFinite(qty) || !Number.isFinite(unit)) return line.charge_amount;
    const raw = line.unit_is_percent ? (qty * unit / 100) : (qty * unit);
    return Math.round(raw * 100) / 100;
};

export const SettlementsHistoryPage = () => {
    const dispatch = useDispatch();
    const { activeOperators } = useSelector(activeOperatorsSelector);

    // ---- Generate-style filters ----
    const [cycle, setCycle] = useState("WEEKLY");
    const [financialYears, setFinancialYears] = useState([]);
    const [selectedFY, setSelectedFY] = useState(deriveCurrentFY());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [week, setWeek] = useState(1);
    const [weekday, setWeekday] = useState("MONDAY");
    const [selectedStatus, setSelectedStatus] = useState(null);

    const { periodStart, periodEnd } = useMemo(
        () => computePeriod(cycle, selectedFY, month, week, weekday),
        [cycle, selectedFY, month, week, weekday]
    );

    // Week options depend on month/weekday so the 6th-week corner case is
    // selectable. Clamp the current selection if the month has fewer weeks.
    const weekOptions = useMemo(
        () => buildWeekOptions(weeksInFYMonth(selectedFY, month, weekday)),
        [selectedFY, month, weekday]
    );
    useEffect(() => {
        if (week > weekOptions.length) setWeek(weekOptions.length);
    }, [weekOptions.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- data ----
    const [loading, setLoading] = useState(false);
    const [settlements, setSettlements] = useState([]);

    // ---- drilldown modal ----
    const [modalLoading, setModalLoading] = useState(false);
    const [openSettlement, setOpenSettlement] = useState(null);  // { header, lines, audit }
    const [actionLoading, setActionLoading] = useState(false);

    // ---- edit-draft state ----
    const [editMode, setEditMode] = useState(false);
    const [editLines, setEditLines] = useState([]);
    const [editOpeningBalance, setEditOpeningBalance] = useState(0);
    const [editSaving, setEditSaving] = useState(false);

    // ---- advance payment modal (top-level, no settlement) ----
    const [advanceModalOpen, setAdvanceModalOpen] = useState(false);

    useEffect(() => {
        dispatch(getActiveOperators());
        listFinancialYears(
            (data) => {
                setFinancialYears(data);
                // Prefer the server-returned newest FY when available.
                if (isValidArray(data)) setSelectedFY(data[0].financial_year);
            },
            (errMsg) => message.error(errMsg)
        );
    }, []);

    const runQuery = () => {
        const filters = {};
        if (selectedFY) filters.financialYear = selectedFY;
        if (selectedStatus) filters.status = selectedStatus;
        if (cycle) filters.cycle = cycle;
        // Weekly: match the EXACT week + settlement-day period so results are
        // scoped to that specific week/day, not every settlement overlapping the
        // date window. Monthly keeps the overlap window.
        if (cycle === "WEEKLY") {
            if (periodStart) filters.periodStart = periodStart;
            if (periodEnd) filters.periodEnd = periodEnd;
        } else {
            if (periodStart) filters.periodFrom = periodStart;
            if (periodEnd) filters.periodTo = periodEnd;
        }
        setLoading(true);
        listSettlements(
            filters,
            (rows) => { setSettlements(rows); setLoading(false); },
            (errMsg) => { setLoading(false); message.error(errMsg); }
        );
    };

    // Auto-run on FY arrival so first paint isn't empty.
    useEffect(() => {
        if (selectedFY) runQuery();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFY]);

    const openDrilldown = (settlementId) => {
        setOpenSettlement({ loading: true });
        setEditMode(false);
        setModalLoading(true);
        getSettlement(
            { id: settlementId },
            (data) => {
                setModalLoading(false);
                setOpenSettlement(data);
            },
            (errMsg) => {
                setModalLoading(false);
                setOpenSettlement(null);
                message.error(errMsg);
            }
        );
    };

    const reloadAndCloseModal = () => {
        setOpenSettlement(null);
        setEditMode(false);
        runQuery();
    };

    const onApprove = () => {
        if (!openSettlement || !openSettlement.header) return;
        setActionLoading(true);
        approveSettlement(
            { id: openSettlement.header.id },
            (_, msg) => {
                setActionLoading(false);
                message.success(msg || "Approved");
                reloadAndCloseModal();
            },
            (errMsg) => { setActionLoading(false); message.error(errMsg); }
        );
    };

    const onCancel = () => {
        if (!openSettlement || !openSettlement.header) return;
        setActionLoading(true);
        cancelSettlement(
            { id: openSettlement.header.id },
            (_, msg) => {
                setActionLoading(false);
                message.success(msg || "Cancelled");
                reloadAndCloseModal();
            },
            (errMsg) => { setActionLoading(false); message.error(errMsg); }
        );
    };

    const onDelete = () => {
        if (!openSettlement || !openSettlement.header) return;
        setActionLoading(true);
        deleteSettlement(
            { id: openSettlement.header.id, reason: "Deleted via admin UI" },
            (_, msg) => {
                setActionLoading(false);
                message.success(msg || "Deleted");
                reloadAndCloseModal();
            },
            (errMsg) => { setActionLoading(false); message.error(errMsg); }
        );
    };

    const onRowDelete = (row) => {
        deleteSettlement(
            { id: row.id, reason: "Deleted via admin UI (row action)" },
            (_, msg) => {
                message.success(msg || "Deleted");
                runQuery();
            },
            (errMsg) => message.error(errMsg)
        );
    };

    // ---- edit-draft handlers ----
    const enterEditMode = () => {
        if (!openSettlement || !openSettlement.header) return;
        setEditLines((openSettlement.lines || []).map((l) => ({ ...l })));
        setEditOpeningBalance(Number(openSettlement.header.opening_balance) || 0);
        setEditMode(true);
    };

    const exitEditMode = () => {
        setEditMode(false);
        setEditLines([]);
    };

    const updateEditLine = (idx, field, value) => {
        setEditLines((prev) => prev.map((l, i) => {
            if (i !== idx) return l;
            const next = { ...l, [field]: value };
            if (field === "quantity" || field === "unit_price") {
                next.charge_amount = deriveChargeIfAuto(next);
            }
            return next;
        }));
    };

    const updateEditLineAmount = (idx, field, value) => {
        setEditLines((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: Number(value) || 0 } : l));
    };

    const addCustomEditLine = () => {
        setEditLines((prev) => [...prev, {
            line_code: "CUSTOM",
            description: "",
            quantity: null,
            unit_price: null,
            charge_amount: 0,
            credit_amount: 0,
            is_custom: true
        }]);
    };

    const removeEditLine = (idx) => {
        setEditLines((prev) => prev.filter((_, i) => i !== idx));
    };

    const editTotals = useMemo(
        () => computeTotals(editLines, editOpeningBalance),
        [editLines, editOpeningBalance]
    );

    const onSaveEdit = () => {
        if (!openSettlement || !openSettlement.header) return;
        const linesForServer = editLines.map((l, idx) => ({
            ...l,
            auto_quantity: l.auto_quantity == null ? l.quantity : l.auto_quantity,
            auto_charge_amount: l.auto_charge_amount == null ? l.charge_amount : l.auto_charge_amount,
            auto_credit_amount: l.auto_credit_amount == null ? l.credit_amount : l.auto_credit_amount,
            sort_order: idx
        }));
        setEditSaving(true);
        updateSettlement({
            id: openSettlement.header.id,
            openingBalance: editOpeningBalance,
            subTotalCharges: editTotals.subTotalCharges,
            subTotalCredits: editTotals.subTotalCredits,
            gstAmount: editTotals.gstAmount,
            invoiceAmount: editTotals.invoiceAmount,
            netSettlement: editTotals.netSettlement,
            isOverridden: true,
            lines: linesForServer
        }, (resp, msg) => {
            setEditSaving(false);
            message.success(msg || "Updated");
            // Refresh the modal contents + the list.
            openDrilldown(openSettlement.header.id);
            runQuery();
        }, (errMsg) => {
            setEditSaving(false);
            message.error(errMsg);
        });
    };

    // ---- table columns ----
    const operatorColumnSearch = useColumnSearch({ dataIndexExt: "operator_name" });

    const columns = useMemo(() => [
        { title: "Settlement #", dataIndex: "settlement_no", width: 200 },
        {
            title: "Operator",
            dataIndex: "operator_name",
            sorter: (a, b) => (a.operator_name || "").localeCompare(b.operator_name || ""),
            ...operatorColumnSearch
        },
        { title: "FY", dataIndex: "financial_year", width: 90 },
        { title: "Cycle", dataIndex: "cycle", width: 90 },
        {
            title: "Kind",
            dataIndex: "kind",
            width: 110,
            render: (k) => {
                const kind = k || "FULL";
                if (kind === "FULL") return <Tag>Full</Tag>;
                if (kind === "WEB_ONLY") return <Tag color="blue">Web Tickets</Tag>;
                if (kind === "OTHER_ONLY") return <Tag color="orange">Other Charges</Tag>;
                return <Tag>{kind}</Tag>;
            }
        },
        {
            title: "Period",
            width: 200,
            render: (_, r) => `${fmtDate(r.period_start)} → ${fmtDate(r.period_end)}`
        },
        {
            title: "Settlement Amt",
            width: 120,
            render: (_, r) => inr(r.invoice_amount)
        },
        {
            title: "Net",
            width: 130,
            render: (_, r) => {
                const n = Number(r.net_settlement);
                const color = n > 0 ? "#389e0d" : n < 0 ? "#cf1322" : "#8c8c8c";
                const label = n > 0 ? `+ ${inr(n)}` : n < 0 ? `− ${inr(-n)}` : inr(0);
                return <Text strong style={{ color, fontSize: 16 }}>{label}</Text>;
            }
        },
        {
            title: "Outstanding",
            width: 120,
            render: (_, r) => inr(r.amount_outstanding)
        },
        {
            title: "Status",
            dataIndex: "status",
            width: 150,
            render: (s) => <Tag color={STATUS_COLORS[s] || "default"}>{s}</Tag>
        },
        {
            title: "Action",
            width: 110,
            render: (_, r) => {
                const rowDeletable = (r.status === "DRAFT" || r.status === "CANCELLED") && checkToCancelSettlement();
                const menu = (
                    <Menu>
                        <Menu.Item key="view" onClick={() => openDrilldown(r.id)}>View / PDF</Menu.Item>
                        {rowDeletable && (
                            <Menu.Item
                                key="delete"
                                danger
                                onClick={() => showConfirm(
                                    () => onRowDelete(r),
                                    "Delete settlement",
                                    `Soft-delete ${r.settlement_no}? It will disappear from the history list (audit log retained).`,
                                    "Delete"
                                )}
                            >
                                Delete
                            </Menu.Item>
                        )}
                    </Menu>
                );
                return (
                    <Space>
                        <Button size="small" onClick={() => openDrilldown(r.id)}>Open</Button>
                        <Dropdown overlay={menu} trigger={["click"]}>
                            <Button size="small" icon={<MoreOutlined />} />
                        </Dropdown>
                    </Space>
                );
            }
        }
    ], [activeOperators]);

    // ---- modal content ----
    const header = openSettlement && openSettlement.header;
    const canApprove = header && header.status === "DRAFT" && !editMode && checkToApproveSettlement();
    const canCancel = header && header.status !== "CANCELLED" && header.status !== "SETTLED" && !editMode && checkToCancelSettlement();
    const canDelete = header && (header.status === "DRAFT" || header.status === "CANCELLED") && !editMode && checkToCancelSettlement();
    const canEdit = header && header.status === "DRAFT" && !editMode && checkToEditSettlement();

    const linesToShow = editMode ? editLines : (openSettlement && openSettlement.lines) || [];
    const displayTotals = editMode ? editTotals : null;

    return (
        <div className="settlement-admin" style={{ margin: 20 }}>
            <Title level={4}>Settlements &mdash; History</Title>

            <div style={{ background: "#fafafa", padding: 16, marginBottom: 16, borderRadius: 4 }}>
                <Row gutter={[12, 12]} align="middle" wrap>
                    <Col><Text strong>Cycle:</Text></Col>
                    <Col>
                        <Radio.Group value={cycle} onChange={(e) => setCycle(e.target.value)}>
                            <Radio.Button value="WEEKLY">Weekly</Radio.Button>
                            <Radio.Button value="MONTHLY">Monthly</Radio.Button>
                        </Radio.Group>
                    </Col>
                    <Col><Text strong>FY:</Text></Col>
                    <Col>
                        <Select value={selectedFY} onChange={setSelectedFY} style={{ width: 130 }}>
                            {(financialYears.length > 0
                                ? financialYears.map((fy) => fy.financial_year)
                                : [selectedFY]
                            ).map((fy) => (
                                <Option key={fy} value={fy}>{fy}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col><Text strong>Month:</Text></Col>
                    <Col>
                        <Select value={month} onChange={setMonth} style={{ width: 140 }}>
                            {FY_MONTH_OPTIONS.map((m) => (
                                <Option key={m.num} value={m.num}>{m.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    {cycle === "WEEKLY" && (
                        <>
                            <Col><Text strong>Settlement day:</Text></Col>
                            <Col>
                                <Select value={weekday} onChange={setWeekday} style={{ width: 130 }}>
                                    {WEEKDAYS.map((w) => <Option key={w} value={w}>{w}</Option>)}
                                </Select>
                            </Col>
                            <Col><Text strong>Week:</Text></Col>
                            <Col>
                                <Select value={week} onChange={setWeek} style={{ width: 120 }}>
                                    {weekOptions.map((w) => (
                                        <Option key={w.num} value={w.num}>{w.label}</Option>
                                    ))}
                                </Select>
                            </Col>
                        </>
                    )}
                    <Col><Text strong>Status:</Text></Col>
                    <Col>
                        <Select
                            value={selectedStatus}
                            onChange={setSelectedStatus}
                            style={{ width: 170 }}
                            allowClear
                            placeholder="All"
                        >
                            {Object.keys(STATUS_COLORS).map((s) => (
                                <Option key={s} value={s}>{s}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Button type="primary" loading={loading} onClick={runQuery}>View</Button>
                    </Col>
                    {checkToRecordSettlementPayment() && (
                        <Col flex="auto" style={{ textAlign: "right" }}>
                            <Button onClick={() => setAdvanceModalOpen(true)}>
                                Record Advance
                            </Button>
                        </Col>
                    )}
                </Row>
                <Row style={{ marginTop: 8 }}>
                    <Col>
                        <Text type="secondary">Period:&nbsp;</Text>
                        {periodStart && periodEnd
                            ? <Text strong>{periodStart} &nbsp;→&nbsp; {periodEnd}</Text>
                            : <Text type="danger">No valid period for this selection</Text>
                        }
                    </Col>
                </Row>
            </div>

            <Table
                rowKey="id"
                dataSource={settlements}
                columns={columns}
                loading={loading}
                pagination={{ pageSize: 25 }}
                scroll={{ x: 1100 }}
                summary={(pageData) => {
                    let weOwe = 0;
                    let toCollect = 0;
                    pageData.forEach((r) => {
                        const n = Number(r.net_settlement) || 0;
                        if (n > 0) weOwe += n;
                        else if (n < 0) toCollect += -n;
                    });
                    return (
                        <Table.Summary.Row style={{ background: "#fafafa" }}>
                            <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                            <Table.Summary.Cell index={1}></Table.Summary.Cell>
                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                            <Table.Summary.Cell index={3}></Table.Summary.Cell>
                            <Table.Summary.Cell index={4}></Table.Summary.Cell>
                            <Table.Summary.Cell index={5}></Table.Summary.Cell>
                            <Table.Summary.Cell index={6}></Table.Summary.Cell>
                            <Table.Summary.Cell index={7}>
                                <Text strong style={{ color: "#389e0d", fontSize: 16, display: "block" }}>
                                    + {inr(weOwe)}
                                </Text>
                                <Text strong style={{ color: "#cf1322", fontSize: 16, display: "block" }}>
                                    − {inr(toCollect)}
                                </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={8}></Table.Summary.Cell>
                            <Table.Summary.Cell index={9}></Table.Summary.Cell>
                            <Table.Summary.Cell index={10}></Table.Summary.Cell>
                        </Table.Summary.Row>
                    );
                }}
            />

            <Modal
                wrapClassName="settlement-modal"
                visible={!!openSettlement}
                onCancel={() => { setOpenSettlement(null); setEditMode(false); }}
                width="min(1100px, 95vw)"
                style={{ top: 16 }}
                bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
                title={header
                    ? (
                        <span>
                            <Text type="secondary">{`${header.settlement_no} — `}</Text>
                            <Text strong style={{ fontSize: 20 }}>{header.operator_name}</Text>
                            {editMode && <Text type="secondary"> (Editing draft)</Text>}
                        </span>
                    )
                    : "Loading…"}
                footer={header ? [
                    editMode && (
                        <Button key="cancel-edit" onClick={exitEditMode} disabled={editSaving}>
                            Cancel edit
                        </Button>
                    ),
                    editMode && (
                        <Button
                            key="save-edit"
                            type="primary"
                            loading={editSaving}
                            onClick={() => showConfirm(
                                onSaveEdit,
                                "Save draft changes",
                                `Overwrite ${header.settlement_no} with the edited lines? It will stay as DRAFT.`,
                                "Save"
                            )}
                        >
                            Save draft
                        </Button>
                    ),
                    canEdit && (
                        <Button key="edit" onClick={enterEditMode}>
                            Edit draft
                        </Button>
                    ),
                    canDelete && (
                        <Button
                            key="delete"
                            danger
                            loading={actionLoading}
                            onClick={() => showConfirm(
                                onDelete,
                                "Delete settlement (soft)",
                                `Soft-delete ${header.settlement_no}? It will disappear from the history list (audit log retained). Only DRAFT / CANCELLED can be deleted.`,
                                "Delete"
                            )}
                        >
                            Delete
                        </Button>
                    ),
                    canCancel && (
                        <Button
                            key="cancel"
                            danger
                            loading={actionLoading}
                            onClick={() => showConfirm(
                                onCancel,
                                "Cancel settlement",
                                `Cancel ${header.settlement_no}? This is reversible only by re-creating the settlement.`,
                                "Cancel settlement"
                            )}
                        >
                            Cancel settlement
                        </Button>
                    ),
                    canApprove && (
                        <Button
                            key="approve"
                            type="primary"
                            loading={actionLoading}
                            onClick={() => showConfirm(
                                onApprove,
                                "Approve settlement",
                                `Approve ${header.settlement_no}? Once approved, line items become read-only here.`,
                                "Approve"
                            )}
                        >
                            Approve
                        </Button>
                    ),
                    <Button key="close" onClick={() => { setOpenSettlement(null); setEditMode(false); }}>Close</Button>
                ].filter(Boolean) : null}
            >
                {modalLoading && <Text>Loading…</Text>}
                {!modalLoading && header && (
                    <div>
                        <Row gutter={16}>
                            <Col span={6}><Text type="secondary">FY</Text><br/><Text strong>{header.financial_year}</Text></Col>
                            <Col span={6}><Text type="secondary">Cycle</Text><br/><Text strong>{header.cycle}</Text></Col>
                            <Col span={6}><Text type="secondary">Period</Text><br/><Text strong>{fmtDate(header.period_start)} → {fmtDate(header.period_end)}</Text></Col>
                            <Col span={6}><Text type="secondary">Status</Text><br/><Tag color={STATUS_COLORS[header.status]}>{header.status}</Tag></Col>
                        </Row>
                        {editMode && (
                            <Row gutter={16} style={{ marginTop: 12 }}>
                                <Col span={8}>
                                    <Text strong>Opening Balance (signed): </Text>
                                    <InputNumber
                                        value={editOpeningBalance}
                                        onChange={(v) => setEditOpeningBalance(Number(v) || 0)}
                                        style={{ width: "100%" }}
                                        step={1}
                                        precision={2}
                                    />
                                    <div><Text type="secondary">+ credit (we owe), − debit (they owe)</Text></div>
                                </Col>
                            </Row>
                        )}
                        <Divider style={{ margin: "12px 0" }} />
                        <Table
                            rowKey={(row, idx) => `${row.id || row.line_code || "row"}-${idx}`}
                            dataSource={linesToShow}
                            pagination={false}
                            size="small"
                            summary={(rows) => {
                                const tQty    = rows.reduce((s, l) => s + (Number(l.quantity)      || 0), 0);
                                const tCharge = rows.reduce((s, l) => s + (Number(l.charge_amount) || 0), 0);
                                const tCredit = rows.reduce((s, l) => s + (Number(l.credit_amount) || 0), 0);
                                const gst = tCharge * 0.18;
                                const invoiceTotal = tCharge + gst;
                                return (
                                    <>
                                        <Table.Summary.Row style={{ background: "#fafafa" }}>
                                            <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}><Text strong>{fmtNum(tQty)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}>—</Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}><Text strong>{inr(tCharge)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}><Text strong>{inr(tCredit)}</Text></Table.Summary.Cell>
                                            {editMode && <Table.Summary.Cell index={5}></Table.Summary.Cell>}
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}><Text strong>GST (18%) on charges</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}><Text strong>{inr(gst)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}></Table.Summary.Cell>
                                            {editMode && <Table.Summary.Cell index={5}></Table.Summary.Cell>}
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}><Text strong>Invoice Amount</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}><Text strong style={{ color: "#cf1322" }}>{inr(invoiceTotal)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}></Table.Summary.Cell>
                                            {editMode && <Table.Summary.Cell index={5}></Table.Summary.Cell>}
                                        </Table.Summary.Row>
                                    </>
                                );
                            }}
                            columns={editMode ? [
                                {
                                    title: "Description",
                                    dataIndex: "description",
                                    render: (v, row, idx) => row.is_custom
                                        ? (
                                            <Input
                                                value={v}
                                                onChange={(e) => updateEditLine(idx, "description", e.target.value)}
                                                placeholder="Custom line description"
                                            />
                                        ) : v
                                },
                                {
                                    title: "Qty",
                                    dataIndex: "quantity",
                                    width: 110,
                                    render: (v, row, idx) => {
                                        const editable = row.is_custom || row.auto_compute;
                                        return editable
                                            ? <InputNumber value={v} onChange={(val) => updateEditLine(idx, "quantity", val)} step={0.01} precision={2} style={{ width: "100%" }} />
                                            : (v == null ? "—" : fmtNum(v));
                                    }
                                },
                                {
                                    title: "Unit",
                                    dataIndex: "unit_price",
                                    width: 110,
                                    render: (v, row, idx) => {
                                        const editable = row.is_custom || row.auto_compute;
                                        if (!editable) return v == null ? "—" : v;
                                        return (
                                            <InputNumber
                                                value={v}
                                                onChange={(val) => updateEditLine(idx, "unit_price", val)}
                                                step={0.01}
                                                precision={4}
                                                style={{ width: "100%" }}
                                                addonAfter={row.unit_is_percent ? "%" : null}
                                            />
                                        );
                                    }
                                },
                                {
                                    title: "Charge",
                                    dataIndex: "charge_amount",
                                    width: 130,
                                    render: (v, row, idx) => (
                                        <InputNumber
                                            value={v}
                                            onChange={(val) => updateEditLineAmount(idx, "charge_amount", val)}
                                            step={0.01}
                                            precision={2}
                                            style={{ width: "100%" }}
                                            placeholder={row.auto_compute ? "auto = qty × unit" : ""}
                                        />
                                    )
                                },
                                {
                                    title: "Credit",
                                    dataIndex: "credit_amount",
                                    width: 130,
                                    render: (v, _, idx) => (
                                        <InputNumber
                                            value={v}
                                            onChange={(val) => updateEditLineAmount(idx, "credit_amount", val)}
                                            step={0.01}
                                            precision={2}
                                            style={{ width: "100%" }}
                                        />
                                    )
                                },
                                {
                                    title: "",
                                    width: 70,
                                    render: (_, row, idx) => row.is_custom
                                        ? <Button danger size="small" onClick={() => removeEditLine(idx)}>Remove</Button>
                                        : null
                                }
                            ] : [
                                { title: "Description", dataIndex: "description" },
                                { title: "Qty", dataIndex: "quantity", width: 100, render: (v) => v == null ? "—" : fmtNum(v) },
                                { title: "Unit", dataIndex: "unit_price", width: 80, render: (v) => v == null ? "—" : v },
                                { title: "Charge", dataIndex: "charge_amount", width: 110, render: inr },
                                { title: "Credit", dataIndex: "credit_amount", width: 110, render: inr }
                            ]}
                        />
                        {editMode && (
                            <div style={{ marginTop: 8 }}>
                                <Button onClick={addCustomEditLine}>+ Add line (custom SKU)</Button>
                                <Text type="secondary" style={{ marginLeft: 12 }}>
                                    Use for one-off charges or credits not covered by the auto-computed lines.
                                </Text>
                            </div>
                        )}
                        <div style={{ marginTop: 16, padding: 12, background: "#fafafa", borderRadius: 4 }}>
                            <Row>
                                <Col span={6}><Text>Sub Total Credits</Text></Col>
                                <Col span={6}><Text strong>{inr(editMode ? displayTotals.subTotalCredits : header.sub_total_credits)}</Text></Col>
                                <Col span={6}><Text>Invoice Amount</Text></Col>
                                <Col span={6}><Text strong>{inr(editMode ? displayTotals.invoiceAmount : header.invoice_amount)}</Text></Col>
                            </Row>
                            <Row>
                                <Col span={6}><Text>Opening Balance</Text></Col>
                                <Col span={6}><Text strong>{inr(editMode ? editOpeningBalance : header.opening_balance)}</Text></Col>
                                <Col span={6}><Text>Net Settlement</Text></Col>
                                <Col span={6}>
                                    <Text strong style={{
                                        fontSize: 18,
                                        color: Number(editMode ? displayTotals.netSettlement : header.net_settlement) > 0
                                            ? "#52c41a"
                                            : Number(editMode ? displayTotals.netSettlement : header.net_settlement) < 0 ? "#f5222d" : undefined
                                    }}>
                                        {inr(editMode ? displayTotals.netSettlement : header.net_settlement)}
                                    </Text>
                                </Col>
                            </Row>
                            {!editMode && (
                                <Row>
                                    <Col span={6}><Text>Paid</Text></Col>
                                    <Col span={6}><Text strong>{inr(header.amount_paid)}</Text></Col>
                                    <Col span={6}><Text>Outstanding</Text></Col>
                                    <Col span={6}><Text strong>{inr(header.amount_outstanding)}</Text></Col>
                                </Row>
                            )}
                        </div>

                        {!editMode && (
                            <>
                                <Divider orientation="left" style={{ marginTop: 24 }}>Settlement PDF</Divider>
                                <SettlementInvoicePdfActions header={header} lines={openSettlement.lines} />

                                <Divider orientation="left" style={{ marginTop: 24 }}>Payments</Divider>
                                <PaymentsPanel
                                    settlementId={header.id}
                                    operatorCode={header.operator_code}
                                    settlementStatus={header.status}
                                    netSettlement={header.net_settlement}
                                    outstanding={header.amount_outstanding}
                                    onChange={() => {
                                        if (header && header.id) openDrilldown(header.id);
                                        runQuery();
                                    }}
                                />

                                <Divider orientation="left" style={{ marginTop: 24 }}>Audit log</Divider>
                                <Table
                                    rowKey="id"
                                    dataSource={openSettlement.audit}
                                    pagination={false}
                                    size="small"
                                    columns={[
                                        { title: "When", dataIndex: "at", width: 160, render: fmtDateTime },
                                        { title: "Event", dataIndex: "event_type", width: 160 },
                                        { title: "From", dataIndex: "from_value", width: 130 },
                                        { title: "To", dataIndex: "to_value", width: 130 },
                                        { title: "Actor", dataIndex: "actor_name" },
                                        { title: "Comments", dataIndex: "comments" }
                                    ]}
                                />
                            </>
                        )}
                    </div>
                )}
            </Modal>

            <RecordPaymentModal
                visible={advanceModalOpen}
                onClose={() => setAdvanceModalOpen(false)}
                onSuccess={() => { runQuery(); }}
                operators={isValidArray(activeOperators) ? activeOperators : []}
            />
        </div>
    );
};
